import "core-js/modules/es.symbol";
import "core-js/modules/es.symbol.description";
import "core-js/modules/es.function.name";
import "core-js/modules/es.object.assign";
import _uniqueId from "lodash/uniqueId";

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import * as React from 'react';
import classNames from 'classnames';
import { AlignBox } from "../Align";
import Box from "../Box";
import Group from "../Group";
import Icon from "../Icon";
import { PressableControl, PressableContext, PressableToggle } from "../Pressable";
import { Body, Text } from "../Text";
import ToggleBoxContext from "./ToggleBoxContext";
import FocusHandler from "../handler/FocusHandler";
import HoverHandler from "../handler/HoverHandler";
var useMemo = React.useMemo;

var getCopyPadding = function getCopyPadding(layout) {
  return layout === 'stacked' ? {
    top: 8
  } : {
    left: 12
  };
};

var getDescriptionColor = function getDescriptionColor(disabled) {
  return disabled ? 'disabled' : 'gray';
};

var renderCopy = function renderCopy(checked, children, color, description, fontLineHeight, fontSize, fontWeight, label, layout, wrap) {
  return /*#__PURE__*/React.createElement(Group, {
    className: "ToggleBoxItem-copy",
    padding: getCopyPadding(layout),
    spacing: 4
  }, label && /*#__PURE__*/React.createElement(Text, {
    className: "ToggleBoxItem-label",
    color: color,
    lineHeight: fontLineHeight,
    size: fontSize,
    weight: fontWeight,
    wrap: wrap
  }, label), description && /*#__PURE__*/React.createElement(Body, {
    color: getDescriptionColor()
  }, description), checked && children && /*#__PURE__*/React.createElement(Box, {
    padding: {
      top: 8
    }
  }, children));
};

var getRadioDotColor = function getRadioDotColor(checked, disabled) {
  if (checked && disabled) {
    return 'white';
  }

  return disabled ? 'gray200' : 'white';
};

var renderRadio = function renderRadio(checked, disabled, isFocused) {
  return /*#__PURE__*/React.createElement(PressableControl, {
    checked: checked,
    disabled: disabled,
    focused: isFocused,
    radius: "circle"
  }, /*#__PURE__*/React.createElement(Box, {
    className: classNames('ToggleBoxItem-dot', {
      'ToggleBoxItem-dot--visible': checked
    }),
    background: getRadioDotColor()
  }));
};

var renderIcon = function renderIcon(color, icon) {
  if (icon && typeof icon === 'function') {
    return icon({
      className: 'ToggleBoxItem-icon',
      color: color
    });
  }

  return /*#__PURE__*/React.createElement(Icon, {
    className: "ToggleBoxItem-icon",
    color: color,
    icon: icon,
    size: 12
  });
};

var getSpacing = function getSpacing(size) {
  if (size === 'small') {
    return 8;
  } else if (size === 'medium') {
    return 12;
  } else {
    return 16;
  }
};

var getPadding = function getPadding(connected, direction, isLast, size) {
  if (isLast || connected) {
    return 0;
  } else if (direction === 'horizontal') {
    return {
      right: getSpacing(size)
    };
  } else if (direction === 'vertical') {
    return {
      bottom: getSpacing(size)
    };
  }

  return null;
};

var getRadius = function getRadius(connected, direction, isFirst, isLast) {
  var isVertical = direction === 'vertical';

  if (connected) {
    if (isFirst) {
      return isVertical ? 'top' : 'left';
    } else if (isLast) {
      return isVertical ? 'bottom' : 'right';
    } else if (!isFirst && !isLast) {
      return 'none';
    } else if (isFirst && isLast) {
      return 'all';
    }
  }

  return 'all';
};

var getLayoutPadding = function getLayoutPadding(size) {
  if (size === 'small') {
    return 12;
  } else if (size === 'medium') {
    return 16;
  } else {
    return 20;
  }
};

var getLayoutProps = function getLayoutProps(fontLineHeight, fontSize, fontWeight, layout) {
  if (layout === 'inline') {
    return {
      reference: {
        lineHeight: fontLineHeight,
        size: fontSize,
        weight: fontWeight
      }
    };
  } else {
    return {
      flex: {
        direction: 'column'
      }
    };
  }
};

export var ToggleBoxItem = function ToggleBoxItem(_ref) {
  var _ref$checked = _ref.checked,
      checked = _ref$checked === void 0 ? false : _ref$checked,
      children = _ref.children,
      className = _ref.className,
      description = _ref.description,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      icon = _ref.icon,
      id = _ref.id,
      _ref$invalid = _ref.invalid,
      invalid = _ref$invalid === void 0 ? false : _ref$invalid,
      label = _ref.label,
      onChange = _ref.onChange,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? 'medium' : _ref$size,
      value = _ref.value,
      _ref$wrap = _ref.wrap,
      wrap = _ref$wrap === void 0 ? true : _ref$wrap;
  var idGenerator = useMemo(function () {
    return id || _uniqueId('ToggleBoxItem-');
  }, [id]);
  return /*#__PURE__*/React.createElement(ToggleBoxContext.Consumer, null, function (_ref2) {
    var connected = _ref2.connected,
        direction = _ref2.direction,
        isFirst = _ref2.isFirst,
        isLast = _ref2.isLast,
        layout = _ref2.layout,
        name = _ref2.name,
        size = _ref2.size;
    var classes = classNames('ToggleBoxItem', {
      'ToggleBoxItem--checked': checked,
      'ToggleBoxItem--isFirst': isFirst,
      'ToggleBoxItem--isLast': isLast
    }, className, {
      'Is--disabled': disabled
    });
    var LayoutElement = layout === 'inline' ? AlignBox : Box;
    return /*#__PURE__*/React.createElement(FocusHandler, null, function (focusProps, inputMethod) {
      var isFocused = inputMethod !== null;
      return /*#__PURE__*/React.createElement(HoverHandler, null, function (hoverProps, isHovering) {
        return /*#__PURE__*/React.createElement(Box, {
          className: classes,
          padding: getPadding(connected, direction, isLast, size)
        }, /*#__PURE__*/React.createElement(PressableToggle, {
          className: "ToggleBoxItem-pressable",
          disabled: disabled,
          focused: icon && isFocused,
          hovered: isHovering,
          invalid: invalid,
          radius: getRadius(connected, direction, isFirst, isLast),
          selected: checked,
          size: size,
          width: "maximized"
        }, /*#__PURE__*/React.createElement(PressableContext.Consumer, null, function (_ref3) {
          var pressableContextClasses = _ref3.pressableContextClasses,
              color = _ref3.color,
              fontLineHeight = _ref3.fontLineHeight,
              fontSize = _ref3.fontSize,
              fontWeight = _ref3.fontWeight,
              padding = _ref3.padding;
          return /*#__PURE__*/React.createElement("label", _extends({}, focusProps, hoverProps, {
            className: classNames('ToggleBoxItem-label', pressableContextClasses),
            htmlFor: idGenerator
          }), /*#__PURE__*/React.createElement("input", {
            className: "ToggleBoxItem-source",
            checked: checked,
            disabled: disabled,
            id: idGenerator,
            name: name,
            onChange: onChange,
            type: "radio",
            value: value
          }), /*#__PURE__*/React.createElement(LayoutElement, _extends({
            className: "ToggleBoxItem-area"
          }, getLayoutProps(fontLineHeight, fontSize, fontWeight, layout), {
            padding: getLayoutPadding(size)
          }), icon ? renderIcon(color, icon) : renderRadio(checked, disabled, isFocused), renderCopy(checked, children, color, description, fontLineHeight, fontSize, fontWeight, label, layout, wrap)));
        })));
      });
    });
  });
};